/** @format */

import React, { FC, useEffect, useState, useRef } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import Icon from "../../core/icon/Icon";
import { cn } from "../../../util/classNameConstructor";
import { TabsProps } from "./Tabs.types";

const Tabs: FC<TabsProps> = ({
  tabs,
  active,
  setActive,
  hideTabText,
  classNames,
  type = "underline",
  orientation = "horizontal",
  withBorder,
}) => {
  const [width, setWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const tabRef = useRef<HTMLButtonElement>(null);

  const handleClick = (tab?: number, index?: number) => {
    setActive?.(tab || (index !== undefined ? index + 1 : 1));
  };

  useEffect(() => {
    if (tabRef.current) {
      if (orientation === "horizontal") {
        setWidth(tabRef.current.offsetWidth);
        setOffset(tabRef.current.offsetLeft);
      } else {
        setWidth(tabRef.current.offsetHeight);
        setOffset(tabRef.current.offsetTop);
      }
    }
  }, [active, orientation, tabs]);

  return (
    <div className={cn("relative h-10 w-full max-w-max", classNames?.wrapper)}>
      <TabsPrimitive.Root
        orientation={orientation}
        value={String(active)}
        className={cn("h-full w-full  rounded-xl", type === "filled" && "bg-surface-subdued")}
      >
        <TabsPrimitive.List
          className={cn(
            "relative flex h-full items-center",
            type === "filled" && "w-full rounded-xl border-2 border-border-subdued bg-grey-300 p-0.5",
            type !== "filled" && "w-auto",
            orientation === "vertical" ? "flex-col" : "flex-row"
          )}
        >
          {(type === "underline" || type === "filled") && (
            <div
              className={cn(
                "absolute z-0 transition-all duration-200 left-0 ",
                orientation === "horizontal"
                  ? cn(
                      type === "underline" 
                        ? "bottom-0 h-[4px]" 
                        : "h-full",
                      type === "filled" ? "bg-white rounded-xl" : "bg-action-primary-default rounded",
                      "transform"
                    )
                  : cn(
                      "left-0.5 w-1",
                      type === "filled" ? "bg-white rounded-xl" : "bg-action-primary-default rounded"
                    )
              )}
              style={{
                width: orientation === "horizontal" ? width : undefined,
                height: orientation === "vertical" ? width : undefined,
                transform: `translate${orientation === "horizontal" ? "X" : "Y"}(${offset}px)`
              }}
            />
          )}
          {tabs.map(({ id, text, tab, icon, dataCy, disabled }, index) => (
            <TabsPrimitive.Trigger
              key={id || text || tab}
              id={id}
              value={String(tab || index + 1)}
              onClick={() => handleClick(tab, index)}
              disabled={disabled}
              ref={active === tab || active === index + 1 ? tabRef : null}
              data-cy={dataCy}
              className={cn(
                "focusable flex h-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 -mx-0.5",
                type === "filled" && "w-full",
                orientation === "vertical" && "flex-col",
                disabled ? "cursor-not-allowed opacity-20" : "cursor-pointer",
                active === tab || active === index + 1 
                  ? "opacity-100" 
                  : "opacity-50 hover:opacity-80",
                type === "filled" && "z-[1]"
              )}
            >
              {icon && <Icon name={icon} />}
              <span className={cn("body-md-bold leading-none", classNames?.tab)}>
                {tabs.length <= 2 || !hideTabText
                  ? text
                  : active === tab || active === index + 1
                    ? text
                    : null}
              </span>
            </TabsPrimitive.Trigger>
          ))}
        </TabsPrimitive.List>
      </TabsPrimitive.Root>
      {withBorder && (
        <div className="bg-border-subdued -mt-[1px] h-[1px] w-full rounded-sm" />
      )}
    </div>
  );
};

export default Tabs;
